import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Building2, RefreshCw, Loader2, Check, X,
  ArrowDownLeft, ArrowUpRight, Search, Link2, User,
  Banknote, Calendar, AlertCircle, CheckCircle2, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankTransactions = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [bankStatus, setBankStatus] = useState(null);
  const [connectedBanks, setConnectedBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, matched, unmatched
  const [showLinkModal, setShowLinkModal] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [matchingPayment, setMatchingPayment] = useState(null);
  const [sendReceipt, setSendReceipt] = useState(true);

  useEffect(() => {
    // Surface any TrueLayer redirect-back error/success so the user knows
    // exactly why their bank connect attempt failed (the callback used to
    // redirect with ?bank_error=xxx but nothing displayed it — the user
    // would just see "no bank connected" again with no explanation).
    try {
      const params = new URLSearchParams(window.location.search);
      const bankError = params.get('bank_error');
      const bankConnected = params.get('bank_connected');
      if (bankError || bankConnected) {
        const ERR_HUMAN = {
          invalid_state: 'Bank link expired before you returned. Please try connecting again.',
          missing_params: 'Bank redirected back without an authorisation code. Try again.',
          expired: 'Authorisation expired. Please retry the connection.',
          access_denied: 'You cancelled the connection at your bank.',
        };
        const message = bankConnected
          ? '✓ Bank connected successfully.'
          : (ERR_HUMAN[bankError] || `Bank connection failed: ${bankError}. Please try again.`);
        // Surface via a window alert as a reliable last resort + persist in
        // sessionStorage so a hard refresh doesn't lose it
        sessionStorage.setItem('bank_connect_message', message);
        // Clean the URL so the alert doesn't fire on every refresh
        params.delete('bank_error');
        params.delete('bank_connected');
        const remaining = params.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash
        );
        // Fire after mount so the user sees it
        setTimeout(() => alert(message), 100);
      }
    } catch {
      /* no-op */
    }
    // Check if a specific bank was selected from SyncCentre
    const preselectedBankId = sessionStorage.getItem('selectedBankId');
    if (preselectedBankId) {
      setSelectedBankId(preselectedBankId);
      sessionStorage.removeItem('selectedBankId'); // Clear after use
    }
    loadBankStatus();
    loadCustomers();
  }, []);

  // Load transactions when bank selection changes
  useEffect(() => {
    if (selectedBankId) {
      loadTransactionsForBank(selectedBankId);
    }
  }, [selectedBankId]);

  const loadBankStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Check TrueLayer status first
      const tlStatusRes = await fetch(`${API_URL}/api/truelayer/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (tlStatusRes.ok) {
        const tlStatus = await tlStatusRes.json();
        
        if (tlStatus.connected && tlStatus.banks && tlStatus.banks.length > 0) {
          // Multiple banks supported
          setConnectedBanks(tlStatus.banks);
          setBankStatus({
            connected: true,
            provider: 'truelayer',
            banks: tlStatus.banks
          });
          
          // Auto-select first bank only if none is pre-selected
          setSelectedBankId(prevSelected => {
            if (prevSelected) {
              // Verify the selected bank still exists
              const bankExists = tlStatus.banks.some(b => b.id === prevSelected);
              return bankExists ? prevSelected : tlStatus.banks[0].id;
            }
            return tlStatus.banks[0].id;
          });
        } else if (tlStatus.configured) {
          setBankStatus({ connected: false, configured: true, provider: 'truelayer' });
        } else {
          // TrueLayer not configured, check GoCardless as fallback
          const gcStatusRes = await fetch(`${API_URL}/api/integrations/bank/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (gcStatusRes.ok) {
            const gcStatus = await gcStatusRes.json();
            setBankStatus({ ...gcStatus, provider: 'gocardless' });
          }
        }
      }
    } catch (err) {
      console.error('Error loading bank status:', err);
      setBankStatus({ connected: false, configured: false });
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionsForBank = async (bankId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Fetch TrueLayer transactions for specific bank (use account_id parameter)
      const txnRes = await fetch(`${API_URL}/api/truelayer/transactions?days=30&account_id=${bankId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (txnRes.ok) {
        const txnData = await txnRes.json();
        setTransactions(txnData.transactions || []);
        setStats({
          total: txnData.total_incoming,
          matched: txnData.matched_count,
          unmatched: txnData.unmatched_count
        });
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    await loadBankStatus();
  };
  
  const handleConnectBank = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/truelayer/connect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.authorization_url) {
          // Redirect to TrueLayer auth
          window.location.href = data.authorization_url;
        }
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to initiate bank connection');
      }
    } catch (err) {
      console.error('Error connecting bank:', err);
      alert('Failed to connect bank');
    } finally {
      setConnecting(false);
    }
  };
  
  const handleDisconnectBank = async () => {
    if (!window.confirm('Are you sure you want to disconnect your bank?')) return;
    
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/truelayer/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setBankStatus({ connected: false, configured: true });
      setTransactions([]);
      setStats({});
    } catch (err) {
      console.error('Error disconnecting:', err);
    }
  };

  const loadCustomers = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/customers?has_balance=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data || []);
      }
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const handleMatchPayment = async (transaction, customerId) => {
    setMatchingPayment(transaction.id);
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Use TrueLayer endpoint
      const res = await fetch(
        `${API_URL}/api/truelayer/match-payment?transaction_id=${transaction.id}&customer_id=${customerId}&amount=${transaction.amount}&send_receipt=${sendReceipt}`, 
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const result = await res.json();
        
        // Update local state
        setTransactions(prev => prev.map(t => 
          t.id === transaction.id 
            ? { 
                ...t, 
                matched: true, 
                matched_customer: {
                  id: customerId,
                  name: result.customer_name
                }
              }
            : t
        ));
        
        // Update stats
        setStats(prev => ({
          ...prev,
          matched: (prev.matched || 0) + 1,
          unmatched: Math.max(0, (prev.unmatched || 0) - 1)
        }));
        
        // Refresh customers list (balance changed)
        loadCustomers();
        
        setShowLinkModal(null);
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to match payment');
      }
    } catch (err) {
      console.error('Error matching payment:', err);
      alert('Failed to match payment');
    } finally {
      setMatchingPayment(null);
    }
  };

  const filteredTransactions = transactions.filter(txn => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const description = (txn.description || txn.debtor_name || '').toLowerCase();
      const merchant = (txn.merchant_name || '').toLowerCase();
      if (!(description.includes(term) || merchant.includes(term))) return false;
    }
    
    // Status filter
    if (filter === 'matched' && !txn.matched) return false;
    if (filter === 'unmatched' && txn.matched) return false;
    
    return true;
  });

  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return true;
    const term = customerSearch.toLowerCase();
    return (
      c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-zinc-600">Loading bank transactions...</p>
        </div>
      </div>
    );
  }

  // Not connected state - TrueLayer connect UI
  if (!bankStatus?.connected) {
    return (
      <div className="h-full flex flex-col bg-zinc-50">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-5">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold">Bank Transactions</h1>
              <p className="text-blue-100">Match incoming payments to customers</p>
            </div>
          </div>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Building2 size={40} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Connect Your Bank</h2>
            <p className="text-zinc-600 mb-6">
              Link your bank account via Open Banking to automatically read incoming transactions and match them to customers on your owing list.
            </p>
            
            {bankStatus?.configured ? (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-800">
                      <p className="font-medium">TrueLayer Ready</p>
                      <p className="mt-1 text-emerald-700">
                        Click the button below to securely connect your bank account via Open Banking.
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleConnectBank}
                  disabled={connecting}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {connecting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Building2 size={20} />
                      Connect Bank Account
                    </>
                  )}
                </button>
                
                <p className="mt-4 text-xs text-zinc-500">
                  Powered by TrueLayer Open Banking. Your credentials are never shared with us.
                </p>
              </>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">TrueLayer Not Configured</p>
                      <p className="mt-1 text-amber-700">
                        To enable bank connections, add your TrueLayer credentials in Settings → Integrations.
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={onBack}
                  className="px-6 py-3 bg-zinc-200 text-zinc-700 rounded-xl font-medium hover:bg-zinc-300 transition-colors"
                >
                  Go Back
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="bank-transactions-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold">Bank Transactions</h1>
                {/* Show current bank info */}
                <p className="text-blue-100">
                  {connectedBanks.find(b => b.id === selectedBankId)?.bank_name || 'Your Bank'} - {connectedBanks.find(b => b.id === selectedBankId)?.account_name || 'Account'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {connectedBanks.length > 1 && (
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
                  {connectedBanks.length} accounts connected
                </span>
              )}
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <RefreshCw size={18} />
                Refresh
              </button>
              <button
                onClick={handleDisconnectBank}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-red-500/80 rounded-xl transition-colors text-sm"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Bank Selector - Show prominently when multiple banks */}
        {connectedBanks.length > 1 && (
          <div className="px-6 pb-4">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {connectedBanks.map(bank => (
                <button
                  key={bank.id}
                  onClick={() => setSelectedBankId(bank.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                    selectedBankId === bank.id
                      ? 'bg-white text-blue-600 shadow-lg'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  data-testid={`bank-selector-${bank.bank_name?.toLowerCase()}`}
                >
                  <Building2 size={16} />
                  {bank.bank_name}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Stats */}
        <div className="px-6 pb-5 grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold">£{(stats.total || 0).toFixed(2)}</p>
            <p className="text-blue-100 text-sm">Total Incoming</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold">{stats.matched || 0}</p>
            <p className="text-blue-100 text-sm">Matched</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold">{stats.unmatched || 0}</p>
            <p className="text-blue-100 text-sm">Unmatched</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 bg-white border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
            <input
              type="text"
              placeholder="Search by description or merchant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex bg-zinc-100 rounded-xl p-1">
            {['all', 'unmatched', 'matched'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f 
                    ? 'bg-white text-zinc-900 shadow' 
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3 max-w-4xl mx-auto">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-zinc-200">
              <Banknote size={48} className="mx-auto mb-4 text-zinc-300" />
              <p className="text-zinc-500 text-lg">No transactions found</p>
              <p className="text-zinc-400 text-sm mt-1">Try adjusting your filters or date range</p>
            </div>
          ) : (
            filteredTransactions.map((txn) => (
              <div 
                key={txn.id}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                  txn.matched ? 'border-emerald-200' : 'border-zinc-200'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        txn.matched ? 'bg-emerald-100' : 'bg-blue-100'
                      }`}>
                        <ArrowDownLeft size={24} className={txn.matched ? 'text-emerald-600' : 'text-blue-600'} />
                      </div>
                      <div>
                        <p className="font-semibold text-zinc-900">
                          {txn.sender_name || txn.merchant_name || txn.description || 'Payment'}
                        </p>
                        <p className="text-sm text-zinc-500 max-w-md truncate">
                          {txn.description !== txn.sender_name ? txn.description : ''} 
                          {txn.category && <span className="text-zinc-400"> • {txn.category}</span>}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
                          <Calendar size={12} />
                          {txn.date}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xl font-bold text-emerald-600">
                        +£{txn.amount.toFixed(2)}
                      </p>
                      {txn.matched ? (
                        <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
                          <CheckCircle2 size={14} />
                          Matched
                        </span>
                      ) : (
                        <button
                          onClick={() => setShowLinkModal(txn)}
                          className="mt-2 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                        >
                          <Link2 size={14} />
                          Match
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {txn.matched && txn.matched_customer && (
                    <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-2 text-sm text-zinc-600">
                      <User size={14} />
                      Matched to: <span className="font-medium">{txn.matched_customer.name}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Match Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-blue-500 p-4 text-white">
              <div className="flex items-center justify-between">
                <h3 className="font-bold">Match Payment</h3>
                <button onClick={() => setShowLinkModal(null)} className="p-1 hover:bg-white/20 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              <p className="text-blue-100 mt-1">
                {showLinkModal.merchant_name || showLinkModal.description || 'Payment'} - £{showLinkModal.amount.toFixed(2)}
              </p>
            </div>
            
            <div className="p-4">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <input
                  type="text"
                  placeholder="Search customers owing..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <label className="flex items-center gap-2.5 p-3 mb-3 bg-emerald-50 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendReceipt}
                  onChange={(e) => setSendReceipt(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="bank-match-send-receipt-checkbox"
                />
                <span className="text-sm text-emerald-900">
                  Email receipt to customer showing which invoices this payment covers
                </span>
              </label>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {filteredCustomers.length === 0 ? (
                  <p className="text-center py-8 text-zinc-500">
                    No customers with outstanding balances
                  </p>
                ) : (
                  filteredCustomers.slice(0, 20).map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => handleMatchPayment(showLinkModal, customer.id)}
                      disabled={matchingPayment === showLinkModal.id}
                      className="w-full flex items-center justify-between p-3 border border-zinc-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all text-left disabled:opacity-50"
                    >
                      <div>
                        <p className="font-medium text-zinc-900">{customer.name}</p>
                        <p className="text-sm text-zinc-500">{customer.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-red-600 font-medium">
                          £{(customer.balance || 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-zinc-400">owing</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankTransactions;
